/**
 * RequiredMark.tsx — the "*" on a required field's label, and the ONE legend
 * that says what it means.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Added 2026-09-25 (CTO, on the
 * clinician prescribe form: "need to have asterisk for mandatory fields; till
 * the 'Directions' is filled it does not enable the main CTA to prescribe, but
 * it's not telling it's missed").
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * TWO AUDIENCES, TWO CARRIERS, ONE FACT.
 *
 *  - SIGHTED: the "*" is real TEXT inside the `<label>`, so it sits on the
 *    label's line, wraps with it, prints, survives a screenshot in a support
 *    ticket and needs no stylesheet to exist. Never a CSS `::after` — that is a
 *    marker that disappears whenever the CSS does, and one a unit test cannot
 *    see.
 *  - SCREEN READER: the "*" is `aria-hidden`, so the field's accessible name
 *    stays exactly its label ("Directions", never "Directions star"). The
 *    control itself carries `aria-required="true"`, which every screen reader
 *    announces as "required" in the user's own language. The asterisk is the
 *    VISUAL half of that one fact; the attribute is the other half, and the
 *    caller must set both (the specs pin both, on exactly the required fields).
 *
 * ⚠️ TESTING NOTE (`testing/requiredFields.ts`): `getByLabelText('Directions')`
 * matches the label's TEXT CONTENT, which includes the hidden "*" — so it no
 * longer finds a marked field. One helper per direction:
 *  - PRESENCE: `requiredLabel('Directions')` (exactly "Directions*"), which
 *    also pins that the marker is there — or role + accessible name, which
 *    excludes it.
 *  - ABSENCE: `anyLabel('Strength')` ("Strength" OR "Strength*"). A plain
 *    string passes vacuously against a MARKED field, and `requiredLabel`
 *    passes vacuously against an UNMARKED one — the rogue free-text Strength
 *    box the I1 guards exist to catch. Never `requiredLabel` in an absence
 *    check.
 *
 * NO new colour: `--gps-danger-text` (6.54:1 on white) is the kit's error ink,
 * the conventional colour for this marker, and colour is never the only carrier
 * — the character itself is.
 * ═════════════════════════════════════════════════════════════════════════════
 */
import type { CSSProperties } from 'react';

/** The legend, word for word. Exported so specs assert the real string. */
export const REQUIRED_LEGEND = 'Fields marked * are required.';

const MARK_STYLE: CSSProperties = {
  color: 'var(--gps-danger-text)',
  fontWeight: 700,
  marginLeft: '2px',
};

/**
 * The marker itself. Rendered INSIDE a `<label>` after the label text, with no
 * whitespace text node before it (the gap is a margin), so the label's text
 * content is exactly `<label>*`.
 */
export function RequiredMark() {
  return (
    <span aria-hidden="true" data-required-mark="" style={MARK_STYLE}>
      *
    </span>
  );
}

/**
 * "Fields marked * are required." — ONE per form, above its fields.
 *
 * The "*" here is deliberately NOT hidden: this sentence is the key to a visual
 * convention, and a screen-reader user who hears it ("fields marked star are
 * required") learns what sighted colleagues are looking at. Their own cue on
 * each control is `aria-required`, which does not depend on this line at all.
 */
export function RequiredLegend({ style }: { style?: CSSProperties }) {
  return (
    <p
      data-required-legend=""
      style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13px', color: 'var(--gps-muted)', ...style }}
    >
      Fields marked <span style={{ ...MARK_STYLE, marginLeft: 0 }}>*</span> are required.
    </p>
  );
}
